import { Reducer } from 'redux'
import { CombinedFundingSummaryResponse } from './fundingApi'

export type AgentStage =
  | 'thinking'
  | 'retrieving'
  | 'using_tool'
  | 'processing'
  | 'responding'
  | 'complete'

export interface CompletedStage {
  stage: AgentStage
  message: string
  timestamp: string
}

/** A file attached to a user message, rendered as a downloadable pill. */
export interface LabraChatMessageDocument {
  filename: string
  s3_uri: string
}

export interface LabraChatMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  isStreaming: boolean
  agentStage: AgentStage | null
  stageMessage: string | null
  completedStages: CompletedStage[]
  timestamp: string
  /** Reveal has caught up and settled — safe to render full markdown even while
   * the stream is still technically open (see streamTypewriter `onIdle`). */
  streamSettled?: boolean
  /** Label of a tool currently running (e.g. "Checking funding runs"), shown
   * while the assistant pauses to call a tool. Null when no tool is active. */
  activeTool?: string | null
  /** Files the user attached to this message, shown as pills under the text. */
  documents?: LabraChatMessageDocument[]
}

/**
 * A follow-up action surfaced by the agent at the end of a response
 * (`next_best_actions` stream event). `label` is the button text, `prompt` is
 * the message sent when the button is clicked.
 */
export interface NextBestAction {
  label: string
  prompt: string
}

export interface LabraChatState {
  messages: LabraChatMessage[]
  sessionId: string
  isLoading: boolean
  isPanelOpen: boolean
  isFullScreen: boolean
  fundingSummary: CombinedFundingSummaryResponse | null
  nextBestActions: NextBestAction[]
  /** True while the answer has streamed and the agent is computing its
   * follow-up actions — drives the "Generating next best actions" affordance
   * shown in the suggestion-chip slot until the chips arrive. */
  isGeneratingNextActions: boolean
  /** True while the chat-history list is shown in place of the conversation. */
  isHistoryOpen: boolean
}

export enum LabraChatActionTypes {
  ADD_MESSAGE = 'LABRA_CHAT_ADD_MESSAGE',
  UPDATE_MESSAGE = 'LABRA_CHAT_UPDATE_MESSAGE',
  SET_MESSAGES = 'LABRA_CHAT_SET_MESSAGES',
  CLEAR_MESSAGES = 'LABRA_CHAT_CLEAR_MESSAGES',
  SET_SESSION_ID = 'LABRA_CHAT_SET_SESSION_ID',
  SET_LOADING = 'LABRA_CHAT_SET_LOADING',
  OPEN_PANEL = 'LABRA_CHAT_OPEN_PANEL',
  CLOSE_PANEL = 'LABRA_CHAT_CLOSE_PANEL',
  FINALIZE_STREAMING = 'LABRA_CHAT_FINALIZE_STREAMING',
  TOGGLE_FULLSCREEN = 'LABRA_CHAT_TOGGLE_FULLSCREEN',
  SET_FUNDING_SUMMARY = 'LABRA_CHAT_SET_FUNDING_SUMMARY',
  SET_NEXT_BEST_ACTIONS = 'LABRA_CHAT_SET_NEXT_BEST_ACTIONS',
  SET_GENERATING_NEXT_ACTIONS = 'LABRA_CHAT_SET_GENERATING_NEXT_ACTIONS',
  OPEN_HISTORY = 'LABRA_CHAT_OPEN_HISTORY',
  CLOSE_HISTORY = 'LABRA_CHAT_CLOSE_HISTORY',
}

type LabraChatAction =
  | { type: LabraChatActionTypes.ADD_MESSAGE; payload: LabraChatMessage }
  | {
      type: LabraChatActionTypes.UPDATE_MESSAGE
      payload: { id: string; updates: Partial<LabraChatMessage> }
    }
  | { type: LabraChatActionTypes.SET_MESSAGES; payload: LabraChatMessage[] }
  | { type: LabraChatActionTypes.CLEAR_MESSAGES }
  | { type: LabraChatActionTypes.SET_SESSION_ID; payload: string }
  | { type: LabraChatActionTypes.SET_LOADING; payload: boolean }
  | { type: LabraChatActionTypes.OPEN_PANEL }
  | { type: LabraChatActionTypes.CLOSE_PANEL }
  | { type: LabraChatActionTypes.FINALIZE_STREAMING }
  | { type: LabraChatActionTypes.TOGGLE_FULLSCREEN }
  | {
      type: LabraChatActionTypes.SET_FUNDING_SUMMARY
      payload: CombinedFundingSummaryResponse | null
    }
  | {
      type: LabraChatActionTypes.SET_NEXT_BEST_ACTIONS
      payload: NextBestAction[]
    }
  | {
      type: LabraChatActionTypes.SET_GENERATING_NEXT_ACTIONS
      payload: boolean
    }
  | { type: LabraChatActionTypes.OPEN_HISTORY }
  | { type: LabraChatActionTypes.CLOSE_HISTORY }

const initialState: LabraChatState = {
  messages: [],
  sessionId: '',
  isLoading: false,
  isPanelOpen: false,
  isFullScreen: false,
  fundingSummary: null,
  nextBestActions: [],
  isGeneratingNextActions: false,
  isHistoryOpen: false,
}

export const reducer: Reducer<LabraChatState, LabraChatAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case LabraChatActionTypes.ADD_MESSAGE:
      return { ...state, messages: [...state.messages, action.payload] }
    case LabraChatActionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.id
            ? { ...msg, ...action.payload.updates }
            : msg
        ),
      }
    case LabraChatActionTypes.SET_MESSAGES:
      return { ...state, messages: action.payload }
    case LabraChatActionTypes.CLEAR_MESSAGES:
      return { ...state, messages: [] }
    case LabraChatActionTypes.SET_SESSION_ID:
      return { ...state, sessionId: action.payload }
    case LabraChatActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload }
    case LabraChatActionTypes.OPEN_PANEL:
      return { ...state, isPanelOpen: true }
    case LabraChatActionTypes.CLOSE_PANEL:
      return {
        ...state,
        isPanelOpen: false,
        isFullScreen: false,
        isHistoryOpen: false,
      }
    case LabraChatActionTypes.TOGGLE_FULLSCREEN:
      return { ...state, isFullScreen: !state.isFullScreen }
    case LabraChatActionTypes.SET_FUNDING_SUMMARY:
      return { ...state, fundingSummary: action.payload }
    case LabraChatActionTypes.SET_NEXT_BEST_ACTIONS:
      return { ...state, nextBestActions: action.payload }
    case LabraChatActionTypes.SET_GENERATING_NEXT_ACTIONS:
      return { ...state, isGeneratingNextActions: action.payload }
    case LabraChatActionTypes.OPEN_HISTORY:
      return { ...state, isHistoryOpen: true }
    case LabraChatActionTypes.CLOSE_HISTORY:
      return { ...state, isHistoryOpen: false }
    case LabraChatActionTypes.FINALIZE_STREAMING:
      // Stop any orphaned in-flight assistant message from spinning forever
      // (e.g. when a new message is sent before the previous one finished).
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.isStreaming
            ? {
                ...msg,
                isStreaming: false,
                agentStage: 'complete',
                stageMessage: null,
              }
            : msg
        ),
      }
    default:
      return state
  }
}
